import type { MaybeN } from "@agrigate/ts-utils/lib/types";
import type { ButtonProps, PortalProps } from "@chakra-ui/react";
import { Button, Menu, Portal } from "@chakra-ui/react";
import type { CSSProperties, ReactElement, ReactNode } from "react";
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";
import { RxHamburgerMenu } from "react-icons/rx";

export interface MenuItemConfig {
    type: "item";
    key: string;
    label: ReactNode;
    icon?: ReactNode;
    onClick?: () => void;
}

export interface MenuSeparatorConfig {
    type: "separator";
    key: string;
}

export interface MenuGroupConfig {
    type: "group";
    placement?: "left" | "right";
    key: string;
    label: string;
    items: MenuItemConfig[];
}

export interface PopoverMenuComboProps {
    icon?: ReactNode;
    label?: ReactNode;
    portal?: boolean | Omit<PortalProps, "children">;
    size?: ButtonProps["size"];
    colorPalette?: ButtonProps["colorPalette"];
    variant?: ButtonProps["variant"];
    options: MaybeN<MenuItemConfig | MenuSeparatorConfig | MenuGroupConfig>[];
    style?: CSSProperties;
    disabled?: boolean;
}

function isDefined<T>(value: T | null | undefined): value is T {
    return value !== null && value !== undefined;
}

export default function PopoverMenuCombo(
    p: PopoverMenuComboProps,
): ReactElement | null {
    const validOptions = p.options.filter(isDefined);
    if (validOptions.length === 0) return null;

    const btnProps = {
        size: p.size ?? "xs",
        colorPalette: p.colorPalette,
        variant: p.variant ?? ("plain" as const),
        style: p.style,
        disabled: p.disabled,
        children: (
            <>
                {p.icon ?? (p.label ? null : <RxHamburgerMenu />)}
                {p.label}
            </>
        ),
    };

    let list = (
        <Menu.Positioner>
            <Menu.Content>
                {validOptions.map(opt =>
                    opt.type === "separator" ? (
                        <Menu.Separator key={opt.key} />
                    ) : opt.type === "group" ? (
                        <Menu.Root
                            key={opt.key}
                            positioning={{
                                placement:
                                    opt.placement === "left"
                                        ? "left-start"
                                        : "right-start",
                                gutter: 2,
                            }}
                        >
                            <Menu.TriggerItem>
                                {opt.placement === "left" ? (
                                    <LuChevronLeft />
                                ) : null}
                                {opt.label}
                                {opt.placement === "left" ? null : (
                                    <LuChevronRight />
                                )}
                            </Menu.TriggerItem>
                            <Portal>
                                <Menu.Positioner>
                                    <Menu.Content>
                                        {opt.items.map(item => (
                                            <Menu.Item
                                                value={item.key}
                                                key={item.key}
                                                onClick={item.onClick}
                                                opacity={item.onClick ? 1 : 0.5}
                                            >
                                                {item.label}
                                            </Menu.Item>
                                        ))}
                                    </Menu.Content>
                                </Menu.Positioner>
                            </Portal>
                        </Menu.Root>
                    ) : (
                        <Menu.Item
                            value={opt.key}
                            key={opt.key}
                            onClick={opt.onClick}
                            opacity={opt.onClick ? 1 : 0.5}
                        >
                            {opt.label}
                        </Menu.Item>
                    ),
                )}
            </Menu.Content>
        </Menu.Positioner>
    );

    if (p.portal) {
        const portalProps = p.portal === true ? null : p.portal;
        list = <Portal {...portalProps}>{list}</Portal>;
    }

    return (
        <Menu.Root>
            <Menu.Trigger asChild>
                <Button {...btnProps} />
            </Menu.Trigger>
            {list}
        </Menu.Root>
    );
}
